import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { resolveTone } from "../ui/StatusBadge";

const storageKey = (companyId) => `onboarding-hidden-${companyId}`;

const readHidden = (companyId) => {
  try {
    return localStorage.getItem(storageKey(companyId)) === "1";
  } catch (e) {
    return false;
  }
};

const writeHidden = (companyId) => {
  try {
    localStorage.setItem(storageKey(companyId), "1");
  } catch (e) {
    /* storage indisponível: só esconde nesta sessão */
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const ok = resolveTone(t, "success", theme.palette.type);
  return {
    root: {
      background: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      padding: 20,
      fontFamily: t.font,
    },
    head: {
      display: "flex",
      alignItems: "flex-start",
      gap: 16,
      flexWrap: "wrap",
    },
    headText: { flex: 1, minWidth: 200 },
    title: { fontSize: 15, fontWeight: 600, color: t.text, margin: 0 },
    subtitle: { fontSize: 13, color: t.textSecondary, margin: "2px 0 0", lineHeight: 1.5 },
    progressRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginTop: 14,
    },
    bar: {
      flex: 1,
      height: 6,
      borderRadius: 999,
      background: t.tint.soft,
      overflow: "hidden",
    },
    barFill: {
      height: "100%",
      borderRadius: 999,
      background: t.brand,
      transition: "width 300ms ease",
    },
    progressLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: t.textSecondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    list: {
      listStyle: "none",
      margin: "14px 0 0",
      padding: 0,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: 8,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      textDecoration: "none",
      color: t.text,
      transition: "border-color 150ms ease, background 150ms ease",
      "&:hover": { borderColor: t.tint.strong, background: t.tint.soft },
      "&:focus-visible": { outline: "none", boxShadow: t.shadow.glow },
    },
    itemDone: {
      opacity: 0.75,
      "&:hover": { background: "transparent", borderColor: t.line },
    },
    check: { flex: "none", display: "flex" },
    checkDone: { color: ok.main },
    checkTodo: { color: t.textMuted },
    itemIcon: {
      flex: "none",
      width: 32,
      height: 32,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: t.tint.soft,
      color: t.brand,
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    itemBody: { flex: 1, minWidth: 0 },
    itemTitle: { fontSize: 13, fontWeight: 600, margin: 0, color: t.text },
    itemTitleDone: { textDecoration: "line-through", color: t.textSecondary },
    itemHint: {
      fontSize: 12,
      color: t.textMuted,
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    arrow: { flex: "none", color: t.textMuted, fontSize: 16 },
  };
});

const ITEMS = [
  { key: "connection", to: "/connections", icon: <WhatsAppIcon /> },
  { key: "queue", to: "/queues", icon: <AccountTreeOutlinedIcon /> },
  { key: "team", to: "/users", icon: <PeopleAltOutlinedIcon /> },
  { key: "quickMessage", to: "/quick-messages", icon: <FlashOnIcon /> },
];

const asArray = (data) => (Array.isArray(data) ? data : []);

/**
 * "Primeiros passos": card com progresso (n/4) exibido no Dashboard para admins
 * enquanto faltar conexão, fila, 2º usuário ou resposta rápida.
 * Some sozinho quando tudo está feito; "Ocultar" persiste em
 * `localStorage["onboarding-hidden-{companyId}"]`.
 */
const OnboardingChecklist = ({ className }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const companyId = user && user.companyId;

  const [hidden, setHidden] = useState(() => (companyId ? readHidden(companyId) : true));
  const [done, setDone] = useState(null); // null = carregando

  useEffect(() => {
    if (!companyId) return undefined;
    setHidden(readHidden(companyId));
  }, [companyId]);

  useEffect(() => {
    if (!companyId || hidden) return undefined;
    let active = true;
    const safe = (p) => p.then((r) => r.data).catch(() => null);
    Promise.all([
      safe(api.get("/whatsapp")),
      safe(api.get("/queue")),
      safe(api.get("/users", { params: { pageNumber: 1 } })),
      safe(api.get("/quick-messages", { params: { pageNumber: 1 } })),
    ]).then(([whatsapps, queues, users, quick]) => {
      if (!active) return;
      const usersCount =
        users && typeof users.count === "number" ? users.count : asArray(users && users.users).length;
      const quickCount =
        quick && typeof quick.count === "number" ? quick.count : asArray(quick && quick.records).length;
      setDone({
        connection: asArray(whatsapps).length > 0,
        queue: asArray(queues).length > 0,
        team: usersCount >= 2,
        quickMessage: quickCount > 0,
      });
    });
    return () => {
      active = false;
    };
  }, [companyId, hidden]);

  const completed = useMemo(() => (done ? ITEMS.filter((i) => done[i.key]).length : 0), [done]);

  if (!companyId || hidden || !done) return null;
  if (completed === ITEMS.length) return null;

  const handleHide = () => {
    writeHidden(companyId);
    setHidden(true);
  };

  const pct = Math.round((completed / ITEMS.length) * 100);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")} aria-label={i18n.t("onboarding.title")}>
      <div className={classes.head}>
        <div className={classes.headText}>
          <p className={classes.title}>{i18n.t("onboarding.title")}</p>
          <p className={classes.subtitle}>{i18n.t("onboarding.subtitle")}</p>
        </div>
        <Button size="small" onClick={handleHide}>
          {i18n.t("onboarding.hide")}
        </Button>
      </div>

      <div className={classes.progressRow}>
        <div className={classes.bar} role="progressbar" aria-valuenow={completed} aria-valuemin={0} aria-valuemax={ITEMS.length}>
          <div className={classes.barFill} style={{ width: `${pct}%` }} />
        </div>
        <span className={classes.progressLabel}>
          {completed}/{ITEMS.length} {i18n.t("onboarding.progress")}
        </span>
      </div>

      <ul className={classes.list}>
        {ITEMS.map((item) => {
          const isDone = Boolean(done[item.key]);
          return (
            <li key={item.key}>
              <RouterLink
                to={item.to}
                className={[classes.item, isDone ? classes.itemDone : null].filter(Boolean).join(" ")}
              >
                <span className={`${classes.check} ${isDone ? classes.checkDone : classes.checkTodo}`}>
                  {isDone ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                </span>
                <span className={classes.itemIcon}>{item.icon}</span>
                <span className={classes.itemBody}>
                  <p className={[classes.itemTitle, isDone ? classes.itemTitleDone : null].filter(Boolean).join(" ")}>
                    {i18n.t(`onboarding.items.${item.key}.title`)}
                  </p>
                  <p className={classes.itemHint}>{i18n.t(`onboarding.items.${item.key}.hint`)}</p>
                </span>
                {!isDone && <ArrowForwardIcon className={classes.arrow} />}
              </RouterLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default OnboardingChecklist;
